/* pdp-subject-fit.js — fit the product-page viewer and its thumbnails to the
   photographed object, not to the photo's canvas.

   Why: a ski photo is a thin object on a wide white canvas. 94% of the skis in
   the catalogue ship as square files (1024 x 1024, 1200 x 1200) in which the ski
   is ~16% wide, and even the 3:4 files put it at 17% (Fischer Curv Noize, 18.09.
   2026). `object-fit: contain` fits the CANVAS to the tile, so on a 802px viewer
   the ski came out 95px wide with empty grey on both sides, and the tile could
   not grow with the viewport because the canvas is square anyway. Only the
   pixels know where the object is, and CSS cannot read pixels — this module
   does, on a 96px copy the CDN serves for ~2 KB, and turns the answer into a
   `transform` (no layout, no shift) that scales the object into the padded
   tile and centres it there.

   Two halves. The maths (subjectBox, subjectTransform, analysisUrl) is pure
   and tested in node (tests/pdp-subject-fit.test.mjs). The DOM half below it
   (boxFor, fitPair) is shared by pdp.js and by the inline script in
   snippets/product-media-gallery.liquid, which decides the first photo as
   soon as the gallery is parsed — long before the deferred pdp.js runs. One
   module instance, so the two never probe the same photo twice. */

/* A pixel is background when it is (near) white or transparent. 238 keeps the
   WebP ringing around a white studio backdrop out of the object, and treats a
   light-grey backdrop (#f0f0f0 = 240) as background too, which is right: the
   viewer blends the photo onto its gradient with `multiply`, so such a backdrop
   is invisible there already. */
export const BACKGROUND_MIN = 238;
export const ALPHA_MIN = 16;

/* An object smaller than this share of the frame in either axis is noise (a
   watermark, a dust speck on an otherwise empty file), not something to zoom
   into. */
export const MIN_SUBJECT = 0.04;

/* No blow-up beyond this. The viewer already loads the largest candidate the
   srcset has (the source file is rarely wider than 1536px), so past ~3x a thin
   object turns to mush. In practice a ski lands at 1.1–1.6. */
export const MAX_SCALE = 3;

/* Scales below this are not worth a transform: the object already fills the
   tile within a rounding error, and skipping keeps the common case (a square
   photo of a helmet or a shoe) byte-identical to before. */
export const MIN_GAIN = 1.03;

/**
 * Bounding box of the non-background pixels, as fractions of the frame.
 * @param {Uint8ClampedArray|number[]} data RGBA bytes, row-major (ImageData.data)
 * @param {number} width
 * @param {number} height
 * @returns {{x0:number,y0:number,x1:number,y1:number}|null} null when the frame
 *   is empty or the object is too small to trust
 */
export function subjectBox(data, width, height) {
  if (!data || width <= 0 || height <= 0 || data.length < width * height * 4) return null;
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < height; y += 1) {
    const row = y * width * 4;
    for (let x = 0; x < width; x += 1) {
      const i = row + x * 4;
      if (data[i + 3] < ALPHA_MIN) continue;
      if (data[i] >= BACKGROUND_MIN && data[i + 1] >= BACKGROUND_MIN && data[i + 2] >= BACKGROUND_MIN) continue;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX < 0) return null;
  const box = { x0: minX / width, y0: minY / height, x1: (maxX + 1) / width, y1: (maxY + 1) / height };
  if (box.x1 - box.x0 < MIN_SUBJECT || box.y1 - box.y0 < MIN_SUBJECT) return null;
  return box;
}

/**
 * The transform that scales the object into the frame's padded content box and
 * centres it, for an <img> that sits in `frame` with `object-fit: contain` and
 * symmetric `pad`. The transform origin is the image's own centre, which is
 * also the frame's centre.
 * @param {{width:number,height:number}} frame the img's border box
 * @param {number} pad the img's padding (one side)
 * @param {{width:number,height:number}} natural the photo's pixel size
 * @param {{x0:number,y0:number,x1:number,y1:number}} box from subjectBox()
 * @returns {{scale:number,x:number,y:number}|null} null when there is nothing
 *   worth doing (object already fills the box, or the inputs are unusable)
 */
export function subjectTransform(frame, pad, natural, box) {
  if (!frame || !natural || !box) return null;
  const cw = frame.width - 2 * pad;
  const ch = frame.height - 2 * pad;
  if (!(cw > 0) || !(ch > 0) || !(natural.width > 0) || !(natural.height > 0)) return null;
  // How `contain` renders the photo inside the content box.
  const ratio = natural.width / natural.height;
  let rw;
  let rh;
  if (ratio >= cw / ch) {
    rw = cw;
    rh = cw / ratio;
  } else {
    rh = ch;
    rw = ch * ratio;
  }
  const sw = (box.x1 - box.x0) * rw;
  const sh = (box.y1 - box.y0) * rh;
  if (!(sw > 0) || !(sh > 0)) return null;
  const scale = Math.min(cw / sw, ch / sh, MAX_SCALE);
  if (scale < MIN_GAIN) return null;
  // Object centre relative to the photo centre, in rendered pixels.
  const dx = ((box.x0 + box.x1) / 2 - 0.5) * rw;
  const dy = ((box.y0 + box.y1) / 2 - 0.5) * rh;
  return { scale, x: -dx * scale, y: -dy * scale };
}

/** CSS value for a subjectTransform() result. */
export const transformValue = ({ scale, x, y }) =>
  `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) scale(${scale.toFixed(3)})`;

/**
 * The analysis copy of a Shopify CDN image URL: same file, `width` pixels wide.
 * Shopify's image_url filter writes the size as a `width=` query parameter, so
 * swapping it keeps the `v=` cache key and every other parameter.
 * @param {string} src the viewer image's `src`
 * @param {number} [width=96]
 * @returns {string|null} null for a src that is not an http(s) URL
 */
export function analysisUrl(src, width = 96) {
  if (!src) return null;
  let url;
  try {
    url = new URL(src, 'https://sport-schindele.de/');
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  url.searchParams.set('width', String(width));
  url.searchParams.delete('height');
  url.searchParams.delete('crop');
  return url.toString();
}

/* ---------------------------------------------------------------------------
   DOM side: probe, remember, apply
--------------------------------------------------------------------------- */

/* A box is a property of the photo, so it is remembered across pages under
   the photo's id: a repeat view decides before its first frame without a
   probe. A null ("nothing to fit") is remembered too. Storage can throw or be
   empty (private window, blocked site data); every access is guarded. */
export const BOX_STORAGE_KEY = 'sportSchindele:subjectBox:';

/** @returns {object|null|undefined} the box, null for "nothing to fit", undefined when unknown */
export function storedBox(photoId) {
  try {
    const raw = window.localStorage.getItem(BOX_STORAGE_KEY + photoId);
    if (raw === null) return undefined;
    const box = JSON.parse(raw);
    return box && typeof box.x0 === 'number' ? box : null;
  } catch {
    return undefined;
  }
}

export function rememberBox(photoId, box) {
  try {
    window.localStorage.setItem(BOX_STORAGE_KEY + photoId, JSON.stringify(box));
  } catch {
    /* nothing to do: the next view measures again */
  }
}

/**
 * Measures the object's box on a 96px copy of the photo.
 * @param {string} src the viewer image's `src`
 * @returns {Promise<object|null>} null when the copy cannot be read
 */
export function probeBox(src) {
  const url = analysisUrl(src);
  if (!url) return Promise.resolve(null);
  const probe = new Image();
  // The CDN allows cross-origin reads; without this the canvas is tainted.
  probe.crossOrigin = 'anonymous';
  // `load`, not `decode()`: Chrome holds a decode() promise back while the
  // tab is in the background (a product opened in a new tab), and drawImage
  // decodes on its own anyway.
  const loaded = new Promise((resolve, reject) => {
    probe.addEventListener('load', resolve, { once: true });
    probe.addEventListener('error', () => reject(new Error('probe failed')), { once: true });
  });
  probe.src = url;
  return loaded
    .then(() => {
      const { naturalWidth: width, naturalHeight: height } = probe;
      if (!width || !height) return null;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      context.drawImage(probe, 0, 0);
      return subjectBox(context.getImageData(0, 0, width, height).data, width, height);
    })
    .catch(() => null);
}

const boxes = new Map();

/**
 * The box of a photo: from this page's cache, from localStorage, or measured
 * once and remembered. Never rejects.
 * @param {string} photoId Shopify media id, without the section prefix
 * @param {string} src the viewer image's `src`
 * @returns {Promise<object|null>}
 */
export function boxFor(photoId, src) {
  if (boxes.has(photoId)) return boxes.get(photoId);
  const stored = storedBox(photoId);
  const promise =
    stored !== undefined
      ? Promise.resolve(stored)
      : probeBox(src).then((box) => {
          rememberBox(photoId, box);
          return box;
        });
  boxes.set(photoId, promise);
  return promise;
}

/* The decision is in for this image, whatever it was. Diagnostic only since
   28.09.2026: the photo is no longer held back until this is set. */
export function releaseImage(img) {
  img.dataset.subjectFit = img.style.transform ? 'fitted' : 'none';
}

/* The photo's pixel size, or the size the markup promised while it loads:
   the ratio is the same, and the ratio is all the transform needs. */
export function naturalSize(viewer) {
  if (viewer.naturalWidth) return { width: viewer.naturalWidth, height: viewer.naturalHeight };
  return { width: parseFloat(viewer.getAttribute('width')), height: parseFloat(viewer.getAttribute('height')) };
}

/**
 * Applies the fit to one image and releases it. A hidden image (a slide that
 * is not active) has no box to measure and stays held until it is shown.
 * @returns {boolean} whether the image was decided
 */
export function fitImage(img, box, natural) {
  if (!box || !natural) {
    img.style.transform = '';
    releaseImage(img);
    return true;
  }
  // Layout size, not getBoundingClientRect(): the rect would include the
  // transform this very function set last time, and a refit (resize, variant
  // change) would then scale the scale.
  const frame = { width: img.offsetWidth, height: img.offsetHeight };
  if (!frame.width || !frame.height) return false;
  const pad = parseFloat(getComputedStyle(img).paddingLeft) || 0;
  const transform = subjectTransform(frame, pad, natural, box);
  // A photo that has loaded is on screen (nothing holds it back any more), so
  // its zoom eases in instead of snapping; one still loading takes the
  // transform at once. Set in the same frame as the transform, so the
  // transition applies to this very change (section-pdp.css, "Subject fit").
  img.toggleAttribute('data-subject-fit-settle', Boolean(img.complete && img.naturalWidth));
  img.style.transform = transform ? transformValue(transform) : '';
  releaseImage(img);
  return true;
}

/**
 * Decides a viewer image and its thumb from the same box.
 * @param {HTMLImageElement} viewer the gallery viewer image (source of the file URL)
 * @param {HTMLImageElement|null} thumb its rail thumbnail, if any
 * @param {string} photoId Shopify media id, without the section prefix
 * @returns {Promise<void>}
 */
export function fitPair(viewer, thumb, photoId) {
  return boxFor(photoId, viewer.getAttribute('src')).then((box) => {
    const natural = naturalSize(viewer);
    fitImage(viewer, box, natural);
    if (thumb) fitImage(thumb, box, natural);
  });
}
